import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useLocation, useNavigate } from "react-router-dom";
import useAccountConfigQuery from "../hooks/data/queries/useAccountConfigQuery";
import LoadingFullScreen from "./LoadingFullScreen";
import useCurrentUserAccount from "../hooks/data/queries/useCurrentUserAccount";
import Layout from "./Layout";
import ErrorFullScreen from "./ErrorFullScreen";
import { useEffect, useRef } from "react";
import { useSnackbar } from "./Snackbar";
import { accountConfigAtom, currentUserAccountAtom } from "../atoms";
import { useAtom } from "jotai";

function Main(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearSnackbars } = useSnackbar();
  const { inProgress } = useMsal();
  const [_, setCurrentUserAccount] = useAtom(currentUserAccountAtom);
  const [__, setAccountConfig] = useAtom(accountConfigAtom);
  const {
    data: currentUserAccount,
    isLoading: isLoadingCurrentUserAccount,
    isError: isCurrentUserAccountError,
  } = useCurrentUserAccount({
    cacheTime: Infinity,
    staleTime: Infinity,
    onSuccess(data) {
      setCurrentUserAccount(data);
    },
  });
  const isComingFromWelcome =
    location.state?.previousLocationPathName?.includes("welcome");
  const {
    isLoading: isLoadingAccountConfig,
    isFetching: isFetchingAccountConfig,
    isError: isAccountConfigError,
  } = useAccountConfigQuery(currentUserAccount?.accountHolderCode, {
    enabled: currentUserAccount?.type === "Customer",
    onSuccess: async (data) => {
      setAccountConfig(data);
      if (
        data.onboardingStatus === "In Progress" ||
        data.onboardingStatus === "KYC Needed"
      ) {
        navigate("/welcome");
        return;
      }

      if (data.merchantStatus === "Closed") {
        navigate("/disabledUser");
      }
    },
  });
  const { instance } = useMsal();
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const INACTIVITY_LIMIT =
    (process.env.REACT_APP_INACTIVITY_TIMEOUT_MINS
      ? parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT_MINS)
      : 15) *
    60 *
    1000; // timeout in minutes

  useEffect(() => {
    clearSnackbars();
  }, [location]);

  useEffect(() => {
    const logoutUser = () => {
      instance.logoutRedirect();
    };

    const resetInactivityTimer = () => {
      if (INACTIVITY_LIMIT === 0) return; // Skip if timeout is set to 0
      localStorage.setItem("lastActivity", Date.now().toString());
    };

    const checkInactivity = () => {
      const lastActivity = parseInt(
        localStorage.getItem("lastActivity") || "0",
        10
      );
      if (Date.now() - lastActivity > INACTIVITY_LIMIT) {
        logoutUser();
      }
    };

    // List of events to consider as activity
    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer)
    );

    // Listen for activity in other tabs
    window.addEventListener("storage", checkInactivity);

    // Start interval to check inactivity
    const intervalId = setInterval(checkInactivity, 1000);

    // Initialize lastActivity
    resetInactivityTimer();

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, resetInactivityTimer)
      );
      window.removeEventListener("storage", checkInactivity);
      clearInterval(intervalId);
    };
  }, [instance]);

  const showLoadingScreen =
    inProgress !== InteractionStatus.None ||
    isLoadingCurrentUserAccount ||
    (currentUserAccount?.type === "Customer" &&
      (isComingFromWelcome ? isFetchingAccountConfig : isLoadingAccountConfig));

  function getContent() {
    if (showLoadingScreen) {
      return <LoadingFullScreen />;
    }

    if (isCurrentUserAccountError || isAccountConfigError) {
      return <ErrorFullScreen />;
    }

    return <Layout />;
  }

  return <div className="relative">{getContent()}</div>;
}

export default Main;
